import mitt from 'mitt';
import { UnwrapNestedRefs } from 'vue';
import Source from '../app/crm/models/Source';
import type { DocumentEditLockReason } from '~/composables/invoicing/useDocumentEditLock';
import type { ImageConstraints } from '~~/utils/imageConstraints';
import InvoiceForm from '~/app/invoicing/classes/InvoiceForm';
import type InvoiceFormSection from '~/app/invoicing/classes/InvoiceFormSection';
import Invoice from '~/app/invoicing/models/Invoice';
import Contact from '~~/app/contact/models/Contact';
import ContactCollection from '~~/app/contact/collections/ContactCollection';
import Lead from '~~/app/crm/models/Lead';
import CalendarEvent from '~~/app/planning/models/CalendarEvent';
import TimeActivity from '~~/app/planning/models/TimeActivity';
import PlanningItem from '~~/app/planning/models/PlanningItem';
import Task from '~~/app/task/models/Task';
import Worksite from '~~/app/worksite/models/Worksite';
import ModelFile from '~~/app/common/models/ModelFile';
import Tenant from '~/app/auth/models/Tenant';
import WorksiteProfitabilityDocument from '~/app/worksite/models/WorksiteProfitabilityDocument';
import BankAccountTransaction from '~/app/bankaccount/models/BankAccountTransaction';
import InvoiceTemplateSection from '@/app/invoicing/models/InvoiceTemplateSection';
import InvoiceTemplate from '@/app/invoicing/models/InvoiceTemplate';
import InvoiceTemplateSectionColumn from '@/app/invoicing/models/InvoiceTemplateSectionColumn';
import InvoiceSection from '@/app/invoicing/models/InvoiceSection';
import InvoiceAnnexPdf from '@/app/invoicing/models/InvoiceAnnexPdf';
import { ModelType } from '@/app/base/schemas/BaseSchema';
import Expense from '~/app/invoicing/models/Expense';
import ExpenseLine from '~/app/invoicing/models/ExpenseLine';
import Professional from '~~/app/marketplace/models/Professional';
import Review from '~~/app/marketplace/models/Review';
import type { ProfessionalMediaTarget } from '~~/app/marketplace/schemas/ProfessionalSchema';
import type { Benefit } from '~/app/auth/enums/TenantSubscriptionPlan';
import type { UpsellTriggerType } from '~/composables/useUpsellAccess';

export type ApplicationEvents = {
    // Modal events
    'modal:open': string;
    'modal:close': string;

    // Marketplace / Visibility events
    'marketplace:professional:form-modal:open': ModalPayload<Professional>;
    'marketplace:professional:form-modal:close': void;
    'marketplace:professional:media-modal:open': { professional: Professional; target: ProfessionalMediaTarget };
    'marketplace:professional:media-modal:close': void;
    'marketplace:professional:created': Professional;
    'marketplace:professional:updated': Professional;
    'marketplace:professional:deleted': Professional;
    // Portfolio (Photos tab) — delete-album confirmation dialog (spec §4.1 / AC2). `callback` runs the actual delete.
    'marketplace:portfolio:destroy-alert:open': { title: string; callback: () => void | Promise<void> };
    'marketplace:portfolio:destroy-alert:close': void;
    // Photos — opens the two-step upload modal (drop → bulk tags, spec §4.4).
    'visibility:photos:upload:open': void;
    // Reviews — manual collection modal (Phase 4 §4.5; modal built in TDM-468, opened from §4.4 CTA and §4.2 empty-state CTA)
    'marketplace:reviews:manual-request-modal:open': void;
    'marketplace:reviews:manual-request-modal:close': void;
    // Review model events (`{prefix}:…`); TDM-467 emits `:updated` after a successful answer send to keep the list in sync.
    'marketplace:review:created': Review;
    'marketplace:review:updated': Review;
    'marketplace:review:deleted': Review;
    // Upsell events
    'upsell:modal:open': { benefit?: Benefit; permissionKey?: string; title?: string; description?: string; type?: UpsellTriggerType; label?: string; crossFamily?: boolean };

    // Contact events
    'contact:contact:context-menu:open': { model: Contact; event: Event };
    'contact:contact:context-menu:close': void;
    'contact:contact:modal:open': ModalPayload<Contact> & { collection?: ContactCollection };
    'contact:contact:destroy-alert:open': { callback: () => void; model: Contact };
    'contact:contact:destroy-alert:close': void;
    'contact:contact:modal:close': void;
    'contact:contact:form-modal:open': ModalPayload<Contact>;
    'contact:contact:form-modal:close': void;
    'contact:contact:created': Contact;
    'contact:contact:updated': Contact;
    // BaseEndpoint.sendEvent() sends the deleted model's id, not the model.
    'contact:contact:deleted': string;
    'contact:contact:import-modal:open': void;
    'contact:contact:auto-select': string;
    'contact:contact:destroy-alert': { callback: () => void; model: Contact };

    // Lead events
    'crm:lead:context-menu:open': { model: Lead; event: Event };
    'crm:lead:context-menu:close': void;
    'crm:lead:modal:open': ModalPayload<Lead>;
    'crm:lead:modal:close': void;
    'crm:lead:form-modal:open': any;
    'crm:lead:form-modal:close': void;
    'crm:lead:quote-modal:open': any;
    'crm:lead:quote-modal:close': void;
    'crm:lead:created': Lead;
    'crm:lead:updated': Lead;
    'crm:lead:deleted': Lead;

    // Source events
    'crm:source:form-modal:open': any;
    'crm:source:form-modal:close': void;
    'crm:source:created': Source;
    'crm:source:updated': Source;
    'crm:source:deleted': Source;

    // Invoice events
    'invoicing:invoice:preview-html:open': {
        invoice: Invoice;
        form: InvoiceForm<any>;
    };
    'invoicing:invoice:preview-html:close': void;
    'invoicing:invoice:link-to-worksite:open': { invoice: Invoice };
    'invoicing:invoice:link-to-worksite:close': void;
    'invoicing:invoice:add-contact-modal:open': { invoice: Invoice };
    'invoicing:invoice:add-contact-modal:close': void;
    'invoicing:invoice:change-status:open': { invoice: Invoice };
    'invoicing:invoice:change-status:close': void;
    'invoicing:invoice:final-invoice:open': { invoice: Invoice };
    'invoicing:invoice:final-invoice:close': void;
    'invoicing:invoice:deposit-invoice:open': { invoice: Invoice };
    'invoicing:invoice:deposit-invoice:close': void;
    'invoicing:invoice:payment:form-modal:open': { invoice: Invoice };
    'invoicing:invoice-discount:form-modal:open': any;
    'invoicing:invoice-discount:form-modal:close': void;
    'invoicing:invoice:sent': Invoice;
    'invoicing:invoice-templates:created': InvoiceTemplate;
    'invoicing:invoice-templates:updated': InvoiceTemplate;
    'invoicing:invoice-templates:deleted': InvoiceTemplate;
    'invoicing:invoice-templates:form-modal:open': any;
    'invoicing:invoice-templates:form-modal:close': void;
    'invoicing:invoice-template-sections:created': InvoiceTemplateSection;
    'invoicing:invoice-template-sections:updated': InvoiceTemplateSection;
    'invoicing:invoice-template-sections:deleted': InvoiceTemplateSection;
    'invoicing:invoice-template-sections:form-modal:open': any;
    'invoicing:invoice-template-sections:form-modal:close': void;
    'invoicing:invoice-template-section-columns:created': InvoiceTemplateSectionColumn;
    'invoicing:invoice-template-section-columns:updated': InvoiceTemplateSectionColumn;
    'invoicing:invoice-template-section-columns:deleted': InvoiceTemplateSectionColumn;
    'invoicing:invoice-template-section-columns:form-modal:open': any;
    'invoicing:invoice-template-section-columns:form-modal:close': void;
    'invoicing:invoice-form:edit-disable:open': { reason: DocumentEditLockReason } | void;
    'invoicing:invoice-form:edit-disable:close': void;
    'invoicing:invoice-form:line:open': { section: InvoiceSection | undefined };
    'invoicing:invoice-form:line:add': { section?: InvoiceSection | undefined };
    'invoicing:invoice-form:line:add-composed-work': { section: InvoiceSection | undefined };
    'invoicing:invoice-annex-pdf:annex-pdf-modal:open': any;
    'invoicing:invoice-annex-pdf:annex-pdf-modal:close': void;
    'invoicing:invoice-annex-pdf:annex-pdf-modal:created': InvoiceAnnexPdf;
    'invoicing:invoice-annex-pdf:annex-pdf-modal:updated': InvoiceAnnexPdf;
    'invoicing:invoice:export-modal:open': any;
    'invoicing:invoice:export-modal:close': void;
    'invoicing:invoice-form:data:open': any;
    'invoicing:invoice-form:data:close': any;
    'invoicing:invoice:updating-condition-alert:open': any;
    'invoicing:invoice:invoice-modal:open': any;
    'invoicing:invoice:invoice-modal:close': any;
    'invoicing:invoice:evidence-modal:open': any;
    'invoicing:invoice:evidence-modal:close': any;
    'invoicing:invoice-form:waste-data:open': any;
    'invoicing:invoice-form:waste-data:close': any;
    'invoicing:invoice:send-sms:open': { invoice?: Invoice };
    'invoicing:invoice:select-contact:open': { invoice?: Invoice };
    'invoicing:invoice:send-payment-reminder:open': { invoice?: Invoice };
    'invoicing:invoice-form:add-section:open': void;
    'invoicing:invoice-form:library:open': void;
    'invoicing:invoice:deleted': string;
    'invoicing:invoice-form:composed-work-line:open': { section: InvoiceSection | undefined };
    'invoicing:invoice:french-vat-certificate:open': { invoice: Invoice };

    // Mobile-only sheets — additive splits of Form/Modals/Data.vue and
    // Form/Modals/AddSection.vue, plus two menus and the Client drawer. Desktop keeps
    // using the modals above unchanged.
    //
    // Only the four sheets that are opened from the document screen are declared here.
    // Purchase order, buyer reference, incentive, waste data and conditions have no event
    // of their own: they are panels of the settings sheet's push/pop stack, reached by
    // `push(panel)` rather than by opening a sheet.
    'invoicing:invoice-form:dates-sheet:open': void;
    'invoicing:invoice-form:dates-sheet:close': void;
    'invoicing:invoice-form:description-sheet:open': void;
    'invoicing:invoice-form:description-sheet:close': void;
    'invoicing:invoice-form:section-sheet:open': { section: InvoiceFormSection };
    'invoicing:invoice-form:section-sheet:close': void;
    'invoicing:invoice-form:insert-sheet:open': { section?: InvoiceSection } | void;
    'invoicing:invoice-form:insert-sheet:close': void;
    // `panel` opens the sheet on that panel rather than on its menu: the summary card's
    // réduction and prime lines (`Form/mobile/Summary.vue`) edit the one they show.
    'invoicing:invoice-form:settings-sheet:open': { panel: 'discount' | 'incentive'; discountId?: string } | void;
    'invoicing:invoice-form:settings-sheet:close': void;
    'invoicing:invoice-form:client-sheet:open': void;
    'invoicing:invoice-form:client-sheet:close': void;

    // Vat rate events
    'invoicing:vat-rate:form-modal:open': void;
    'invoicing:vat-rate:form-modal:close': void;

    // Expense events
    'invoicing:expense:mark-as-paid-modal:open': Expense;
    'invoicing:expense:mark-as-paid-modal:close': void;
    'invoicing:expense:updated': Expense;
    'invoicing:expense:created': Expense;
    'invoicing:expense-lines:updated': ExpenseLine;
    'invoicing:expense-lines:created': ExpenseLine;
    'invoicing:expense-lines:deleted': ExpenseLine;
    'invoicing:expense:add-contact-modal:open': Expense;
    'invoicing:expense:add-contact-modal:close': void;
    'invoicing:expense:add-worksite-modal:open': Expense;
    'invoicing:expense:add-worksite-modal:close': void;
    'invoicing:expense:export-modal:open': void;
    'invoicing:expense:export-modal:close': void;

    // Task events
    'task:task:context-menu:open': { model: Task; event: Event };
    'task:task:context-menu:close': void;
    'task:task:modal:open': ModalPayload<Task>;
    'task:task:modal:close': void;
    'task:task:form-modal:open': any;
    'task:task:form-modal:close': void;
    'task:task:created': Task;
    'task:task:updated': Task;
    'task:task:deleted': Task;

    // Tenancy events
    // `redirectTo` lets the caller decide where the page lands once the tenant switched to live
    // mode (the switch always does a full reload). Defaults to the dashboard when omitted.
    'tenancy:tenant:mode:modal:open': { tenant: UnwrapNestedRefs<Tenant>; redirectTo?: string };
    'tenancy:tenants:spots-modal:open': {
        tenant: Tenant;
    };
    'tenancy:tenants:spots-modal:close': void;

    // Planning events
    'planning:time-activity:context-menu:open': {
        model: TimeActivity;
        event: Event;
    };
    'planning:time-activity:context-menu:close': void;
    'planning:time-activity:modal:open': ModalPayload<TimeActivity>;
    'planning:time-activity:modal:close': void;
    'planning:time-activity:form-modal:open': any;
    'planning:time-activity:form-modal:close': void;
    'planning:time-activity:created': TimeActivity;
    'planning:time-activity:updated': TimeActivity;
    'planning:time-activity:deleted': TimeActivity;

    'planning:time-activity:link-invoice:open': ModalPayload<TimeActivity>;
    'planning:time-activity:link-invoice:close': void;
    'planning:time-activity:invoice-multiple:open': any;
    'planning:time-activity:invoice-multiple:close': void;

    // Calendar events
    'planning:calendar-event:context-menu:open': {
        model: CalendarEvent;
        event: Event;
    };
    'planning:calendar-event:context-menu:close': void;
    'planning:calendar-event:modal:open': ModalPayload<CalendarEvent>;
    'planning:calendar-event:modal:close': void;
    'planning:calendar-event:form-modal:open': any;
    'planning:calendar-event:form-modal:close': void;
    'planning:calendar-event:created': CalendarEvent;
    'planning:calendar-event:updated': CalendarEvent;
    'planning:calendar-event:deleted': CalendarEvent;

    'planning:planning-item:created': PlanningItem;
    'planning:planning-item:updated': PlanningItem;
    'planning:planning-item:deleted': string;

    'worksite:worksite:context-menu:open': { model: Worksite; event: Event };
    'worksite:worksite:context-menu:close': void;
    'worksite:worksite:form-modal:open': any;
    'worksite:worksite:form-modal:close': void;
    'worksite:worksite:created': Worksite;
    'worksite:worksite:updated': Worksite;
    'worksite:worksite:deleted': Worksite;
    'worksite:worksite:collection:updated': void;
    'worksite:worksite-profitability-document:form-modal:open': any;
    'worksite:worksite-profitability-document:form-modal:close': void;
    'worksite:worksite-profitability-document:context-menu:open': { model: WorksiteProfitabilityDocument; event: Event };
    'worksite:worksite-profitability-document:context-menu:close': void;
    'worksite:worksite-profitability-document:created': WorksiteProfitabilityDocument;
    'worksite:worksite-profitability-document:updated': WorksiteProfitabilityDocument;
    'worksite:worksite-profitability-document:deleted': WorksiteProfitabilityDocument;
    'worksite:worksite:closure-report:form-modal:open': { worksite?: Worksite };
    'worksite:worksite:closure-report:form-modal:close': void;
    'worksite:worksite:closure-report:send-email:open': any;
    'worksite:worksite:closure-report:send-email:close': void;
    'worksite:worksite:closure-report:mark-as-sent:open': any;
    'worksite:worksite:closure-report:mark-as-sent:close': void;
    'worksite:worksite:closure-reports:updated': any;
    'worksite:worksite:closure-reports:deleted': any;
    'worksite:worksite:closure-reports:created': any;
    'worksite:worksite-progress-billing:quote-edit-disable-info:open': void;
    'worksite:worksite-progress-billing:quote-edit-disable-info:close': { isConfirmed: boolean };
    'worksite:profitability:expense-category-modal:open': { title: string; items: WorksiteProfitabilityDocument[] };
    'worksite:profitability:expense-category-modal:close': void;

    // Model file events
    'common:model-file:context-menu:open': {
        model: ModelFile;
        event: Event;
    };
    'common:model-file:context-menu:close': void;
    'common:model-file:modal:open': ModalPayload<ModelFile>;
    'common:model-file:modal:close': void;
    'common:model-file:form-modal:open': any;
    'common:model-file:form-modal:upload': any;
    'common:model-file:form-modal:close': void;
    'common:model-file:created': ModelFile;
    'common:model-file:updated': ModelFile;
    'common:model-file:deleted': ModelFile;

    // Meilisearch events
    'common:meilisearch-hit:context-menu:open': {
        event: MouseEvent;
        uuid: string;
        modelType: ModelType;
    };
    'common:meilisearch-hit:context-menu:close': void;

    // Files events
    'common:files:export-modal:open': void;
    'common:files:export-modal:close': void;
    'common:files:export-instruction-modal:open': void;
    'common:files:export-instruction-modal:close': void;
    'common:files:add-file-modal:open': {
        files?: { type: 'include' | 'exclude'; extensions: string[] };
        title?: string;
        skipLabel?: string;
        acceptLabel?: string;
        multiple?: boolean;
        imageConstraints?: ImageConstraints;
    } | void;
    'common:files:add-file-modal:close': void;
    'common:files:add-file-modal:files-selected': File[];
    'common:files:add-file-modal:skip': void;

    // AI assistant events
    'ai:assistant:modal:open': void;
    'ai:assistant:modal:close': void;
    'invoicing:invoice:ai-refresh': void;
    'ai:document-created': { id: string; type: string };
    'ai:entity-created': { type: string; id: string; display_name?: string };
    'ai:entity-modified': { type: string; id: string };
    'ai:conversation-created': { id: string };
    'ai:conversation-updated': { id: string; title: string | null };
    'ai:toggle-context': void;

    // App events
    // Sidebar nav item re-clicked while already on that module's page (payload: module key).
    // Lets the current page react without re-navigating — e.g. Messages clears the open thread.
    'app:nav:reselected': string;
    'app:search:open': void;
    'app:search:close': void;
    'app:konami-code-modal:open': void;
    'app:konami-code-modal:close': void;
    'app:coupons:open': void;
    'app:coupons:close': { coupons: string[] } | void;

    // Bank account events
    'bankaccount:bank-account-transaction:assign-invoice:open': {
        transaction: BankAccountTransaction;
    };
    'bankaccount:bank-account-transaction:assign-invoice:close': void;

    // Settings events
    'settings:tenants:subscription:open': {
        tenant: Tenant;
        callback: () => void;
    };
    'settings:tenants:subscription:close': void;
    'settings:credit:credit-modal:open': void;
    'settings:credit:credit-modal:close': void;

    // Waste center events
    'common:waste-center:form-modal:open': void;
    'common:waste-center:form-modal:close': void;

    // Inventory events
    'inventory:product-category:form-modal:open': void;
    'inventory:product-category:form-modal:close': void;
    'inventory:product-section:form-modal:open': void;
    'inventory:product-section:form-modal:close': void;
    'inventory:product-unit:form-modal:open': void;
    'inventory:product-unit:form-modal:close': void;

    // Terms of service events — open with a terms id (authed accept flow) or { locale } (read-only view)
    'common:terms-of-service:accept-modal:open': string | { locale: string };
    'common:terms-of-service:accept-modal:close': void;

    // e-invoicing FR — transfer confirmation dialog (TDD-5194). Mounted inside the reception card.
    'einvoicing:fr:transfer-confirm-modal:open': void;
    'einvoicing:fr:transfer-confirm-modal:close': void;
};

const emitter = mitt<ApplicationEvents>();

export const useEvent = emitter.emit;
export const useListen = emitter.on;
export const useIgnore = emitter.off;
